import { EyeIcon, LinkIcon, PenIcon, UnlinkIcon, WriteIcon } from "@xola/icons";
import { Badge, Dot, Popover, PopoverList, Spinner, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, DemographicIcon, formatDate, isExperiencePurchaseItem, SecondaryButton } from "common";
import { find, noop } from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DigitalWaiver, Guest, Permission, Purchase, PurchaseItem, Waiver, WaiverParticipant } from "types";
import { api } from "../../../api/api";
import { useProduct } from "../../../hooks/resources/useProduct";
import { useModal, useVisibility } from "../../../hooks/useVisibility";
import { generateWaiverSignInURL, getLinkedWaiver, useIsParticipantMinor } from "../../../models/Waiver";
import { updateCache } from "../../../utils/updateCache";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { ConfirmWaiverSwapModal } from "../../Dashboard/Roster/ConfirmWaiverSwapModal";
import { LinkWaiverParticipantModal } from "./LinkWaiverParticipantModal";
import { WaiverCorrectionModal } from "./WaiverCorrectionModal";
import { xolaWaiversApi } from "@/api/waiver";
import { config } from "@/config";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";

interface LinkedWaiverParticipantPopoverProps {
    purchaseItem: PurchaseItem;
    guest: Guest;
    hideLinkWaiverButton?: boolean;
    showLinkText?: boolean;
    appendTo?: ReactNode;
}

export const LinkedWaiverParticipantPopover = ({
    guest: initialGuest,
    purchaseItem,
    hideLinkWaiverButton = false,
    showLinkText = false,
    appendTo,
}: LinkedWaiverParticipantPopoverProps) => {
    const { product } = useProduct(purchaseItem.product.id);
    const [guest, setGuest] = useState(initialGuest);

    const { isGranted } = useAuthorization();

    const { data: waiverTemplates, isLoading: isLoadingWaivers } = useCollection<DigitalWaiver>(
        "waiverTemplates",
        {},
        { enabled: isGranted(Permission.WAIVERS_VIEW) },
    );

    const invalidate = useInvalidate();
    const queryClient = useQueryClient();

    const isWaiversExist = waiverTemplates.length > 0 && !isLoadingWaivers;

    useEffect(() => {
        setGuest(initialGuest);
    }, [initialGuest, setGuest]);

    const [visible, { show: showParticipantPopover, hide: hideParticipantPopover }] = useVisibility(false);
    const [linkPurchaseWaiverModalVisible, { toggle: toggleLinkPurchaseWaiverModal }] = useVisibility(false);
    const editWaiverCorrectionModal = useModal(false);
    const linkedPurchaseWaivers = purchaseItem?.waivers ?? [];
    // Waiver linked to Current Guest level
    const linkedWaiver = getLinkedWaiver(guest.waiver?.id, purchaseItem?.waivers);
    const linkedWaiverParticipant = find(linkedWaiver?.participants, { id: guest?.participant });
    const [participantToCorrect, setParticipantToCorrect] = useState<WaiverParticipant>();

    // Update waiver mutation
    const { mutateAsync: updateWaiver, isLoading: isUpdatingWaiver } = useMutation(
        async (updatedWaiver: Waiver) => {
            const { data } = await api.patch<Waiver>(`/waivers/${updatedWaiver.id}/participants`, updatedWaiver);
            return data;
        },
        {
            onSuccess: (updatedWaiver) => {
                invalidate("waivers", updatedWaiver.id);
                queryClient.invalidateQueries(["audits", { objectClass: "waiver", objectId: updatedWaiver.id }]);
                queryClient.invalidateQueries(["purchases", purchaseItem.purchase.id, { expand: "waiver" }]);
            },
        },
    );

    const { mutateAsync: updateXolaWaiver, isLoading: isUpdatingXolaWaiver } = useMutation(
        async (updatedParticipant: WaiverParticipant) => {
            const { data } = await xolaWaiversApi.put<Waiver>(`/waivers/${linkedWaiver?.remoteId}`, {
                participants: [updatedParticipant],
            });
            return data;
        },
    );

    const openCorrectionModal = (participant: WaiverParticipant) => {
        setParticipantToCorrect(participant);
        editWaiverCorrectionModal.show();
    };

    const closeCorrectionModal = () => {
        editWaiverCorrectionModal.hide();
        setParticipantToCorrect(undefined);
    };

    const handleWaiverCorrectionSubmit = async (updatedParticipant: WaiverParticipant) => {
        if (!linkedWaiver) {
            return;
        }

        // Create a copy of the current waiver
        const updatedWaiver = {
            ...linkedWaiver,
            purchaseId: purchaseItem.purchase.id,
            participants: linkedWaiver.participants.map((participant) =>
                participant.id === updatedParticipant.id ? updatedParticipant : participant,
            ),
        };

        await Promise.all([updateXolaWaiver(updatedParticipant), updateWaiver(updatedWaiver)]);

        setParticipantToCorrect(undefined);
        editWaiverCorrectionModal.hide();
    };

    const handleSignNewWaiverClick = () => {
        if (product?.waiverPreference && isExperiencePurchaseItem(purchaseItem)) {
            const url = generateWaiverSignInURL(product.waiverPreference, purchaseItem.purchase.id, purchaseItem);
            window.open(url, "_blank");
        }
    };

    const linkNewPurchaseWaiver = () => {
        toggleLinkPurchaseWaiverModal();
        hideParticipantPopover();
    };

    const { mutateAsync: updatePurchase, isLoading: isUpdatingPurchase } = useMutation(async (payload: any) => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchaseItem.purchase.id}`, payload);
        return data;
    });

    const handleParticipantLinking = async (waiver: Waiver, participant: WaiverParticipant, unlink?: boolean) => {
        // Removing waiver reference from the guests
        const guests: Guest[] = [];

        if (unlink) {
            // Unlink if already linked participant
            // remove all the fields except for the fields required for unlinking
            const updatedGuest = { id: guest.id, participant: guest.participant, waiver: null };
            guests.push(updatedGuest as Guest);
        } else {
            const linkedGuest = getLinkedGuest(participant, purchaseItem.guests);
            if (linkedGuest) {
                // If this Participant already linked to different Guest, unlink from that Guest
                const updatedGuest = { id: linkedGuest.id, participant: linkedGuest.participant, waiver: null };
                guests.push(updatedGuest as Guest);
            }

            // Link new Participant to this Guest
            guests.push({
                ...guest,
                waiver: { id: waiver.id },
                participant: participant.id,
            });
        }

        const updatedPurchase = await updatePurchase({ items: [{ id: purchaseItem.id, guests }] });
        const updatedPurchaseItem = find(updatedPurchase.items, { id: purchaseItem.id });
        const currentGuest = find(updatedPurchaseItem?.guests, { id: guest.id });
        if (currentGuest) {
            setGuest(currentGuest);
        }

        updateCache("purchases", updatedPurchase);
        hideParticipantPopover();
    };

    const shouldShowWaiverCorrectionModal = editWaiverCorrectionModal.isVisible && participantToCorrect;

    return (
        <>
            <Popover
                visible={visible}
                zIndex={30}
                offset={[0, 18]}
                appendTo={appendTo}
                className={clsx(
                    "w-80 overflow-x-visible border-b-transparent pt-4",
                    linkedWaiverParticipant && "h-16 pb-4",
                )}
                placement="bottom"
                onClickOutside={hideParticipantPopover}
            >
                {showLinkText
                    ? isWaiversExist && (
                          <Tooltip content="Link Waivers">
                              <SecondaryButton
                                  size="small"
                                  onClick={visible ? hideParticipantPopover : showParticipantPopover}
                              >
                                  Link
                              </SecondaryButton>
                          </Tooltip>
                      )
                    : isWaiversExist && (
                          <Tooltip content="View Waivers">
                              <SecondaryButton
                                  size="tiny"
                                  onClick={visible ? hideParticipantPopover : showParticipantPopover}
                              >
                                  <Dot
                                      className="mr-1 hover:bg-white"
                                      color={linkedWaiverParticipant ? "success" : "danger"}
                                  />
                                  <WriteIcon />
                              </SecondaryButton>
                          </Tooltip>
                      )}
                <Popover.Content>
                    {linkedWaiver && linkedWaiverParticipant ? (
                        <div className="max-h-96 overflow-y-scroll">
                            <PopoverList.Item
                                key={linkedWaiverParticipant.id}
                                name={linkedWaiverParticipant.id}
                                className="cursor-auto !border-white py-1 font-medium hover:bg-transparent"
                                onClickItem={noop}
                            >
                                <WaiverParticipantItem
                                    isLinked
                                    isSaving={isUpdatingPurchase}
                                    waiver={linkedWaiver}
                                    currentGuest={guest}
                                    participant={linkedWaiverParticipant}
                                    handleParticipantLinking={handleParticipantLinking}
                                    onChange={openCorrectionModal}
                                />
                            </PopoverList.Item>
                        </div>
                    ) : (
                        <div className="max-h-96 overflow-y-scroll">
                            {linkedPurchaseWaivers?.map((waiver: Waiver) =>
                                waiver.participants?.map((participant) => (
                                    <PopoverList.Item
                                        key={participant.id}
                                        name={participant.id}
                                        className="cursor-auto !border-white py-1 font-medium hover:bg-transparent"
                                        onClickItem={noop}
                                    >
                                        <WaiverParticipantItem
                                            isSaving={isUpdatingPurchase}
                                            waiver={waiver}
                                            currentGuest={guest}
                                            participant={participant}
                                            guests={purchaseItem.guests}
                                            handleParticipantLinking={handleParticipantLinking}
                                            onChange={openCorrectionModal}
                                        />
                                    </PopoverList.Item>
                                )),
                            )}
                        </div>
                    )}

                    {!linkedWaiverParticipant && (
                        <PopoverList.Item
                            name="signNewOrLinkWaiver"
                            className="flex flex-col space-y-1 space-x-0 !border-white bg-white pt-4 pb-4"
                            onClickItem={noop}
                        >
                            {product?.waiverPreference && (
                                <SecondaryButton className="w-full" onClick={handleSignNewWaiverClick}>
                                    Sign New Waiver
                                </SecondaryButton>
                            )}

                            {!hideLinkWaiverButton && (
                                <SecondaryButton className="w-full" onClick={linkNewPurchaseWaiver}>
                                    Link Waiver
                                </SecondaryButton>
                            )}
                        </PopoverList.Item>
                    )}
                </Popover.Content>
            </Popover>

            {shouldShowWaiverCorrectionModal && (
                <WaiverCorrectionModal
                    isOpen={editWaiverCorrectionModal.isVisible}
                    participant={participantToCorrect}
                    isSaving={isUpdatingWaiver || isUpdatingXolaWaiver}
                    onClose={closeCorrectionModal}
                    onSubmit={handleWaiverCorrectionSubmit}
                />
            )}

            <LinkWaiverParticipantModal
                isOpen={linkPurchaseWaiverModalVisible}
                guest={guest}
                purchaseItem={purchaseItem}
                onClose={toggleLinkPurchaseWaiverModal}
            />
        </>
    );
};

interface WaiverParticipantItemProps {
    waiver: Waiver;
    participant: WaiverParticipant;
    isSaving: boolean;
    currentGuest: Guest;
    guests?: Guest[];
    isLinked?: boolean;
    handleParticipantLinking: (waiver: Waiver, participant: WaiverParticipant, unlink?: boolean) => void;
    onChange: (participant: WaiverParticipant) => void;
}

const WaiverParticipantItem = ({
    waiver,
    participant,
    isSaving,
    currentGuest,
    guests,
    isLinked,
    handleParticipantLinking,
    onChange,
}: WaiverParticipantItemProps) => {
    const linkedGuest = getLinkedGuest(participant, guests);
    const [isWaiverSwapModalVisible, { toggle: toggleConfirmWaiverSwapModal }] = useVisibility(false);
    const [isSelected, setIsSelected] = useState(false);

    const handleLinkParticipant = () => {
        if (linkedGuest) {
            toggleConfirmWaiverSwapModal();
            return;
        }

        setIsSelected(true);
        handleParticipantLinking(waiver, participant);
    };

    const handleConfirmSwap = () => {
        handleParticipantLinking(waiver, participant);
        toggleConfirmWaiverSwapModal();
    };

    const handleCorrectWaiver = () => {
        onChange(participant);
    };

    const isXolaWaiver = waiver.remoteSystem === config.xolaWaivers.pluginUserId;
    const isMinor = useIsParticipantMinor(participant, waiver.waiverTypeId);

    return (
        <>
            <span className="mr-auto">
                <Tooltip
                    content={isMinor ? `Minor, DOB: ${formatDate(participant.dateOfBirth, DateFormat.DATE)}` : ""}
                    disabled={!isMinor}
                >
                    <DemographicIcon name={isMinor ? "child" : "adult"} size="tiny" className="mr-1" />
                    <NoTranslate>{participant.customerName}</NoTranslate>
                </Tooltip>
                {!!linkedGuest && (
                    <Badge color="danger" size="small" className="ml-1 rounded-md">
                        Linked to <NoTranslate>{linkedGuest.name}</NoTranslate>
                    </Badge>
                )}
            </span>
            {isLinked && isXolaWaiver && (
                <Tooltip content="Waiver corrections">
                    <SecondaryButton disabled={isSaving} size="tiny" onClick={handleCorrectWaiver}>
                        <PenIcon />
                    </SecondaryButton>
                </Tooltip>
            )}

            {isLinked ? (
                <InlineConfirmDeletePopover
                    placement="left"
                    confirmButtonText="Confirm"
                    onConfirm={() => handleParticipantLinking(waiver, participant, true)}
                >
                    <Tooltip content="Unlink waiver">
                        <SecondaryButton disabled={isSaving} size="tiny">
                            {isSaving ? <Spinner size="tiny" /> : <UnlinkIcon />}
                        </SecondaryButton>
                    </Tooltip>
                </InlineConfirmDeletePopover>
            ) : (
                <span className="flex max-h-5 items-center">
                    <Tooltip content="Link waiver">
                        <SecondaryButton disabled={isSaving} size="tiny" onClick={handleLinkParticipant}>
                            {isSaving && isSelected ? <Spinner size="tiny" /> : <LinkIcon />}
                        </SecondaryButton>
                    </Tooltip>
                </span>
            )}

            {isLinked && (
                <Tooltip content="Preview Waiver">
                    <SecondaryButton size="tiny" onClick={() => window.open(waiver.url, "_blank")}>
                        <EyeIcon size="tiny" />
                    </SecondaryButton>
                </Tooltip>
            )}

            {linkedGuest && (
                <ConfirmWaiverSwapModal
                    isOpen={isWaiverSwapModalVisible}
                    swapFromGuest={linkedGuest.name}
                    swapToGuest={currentGuest.name}
                    onClose={toggleConfirmWaiverSwapModal}
                    onConfirm={handleConfirmSwap}
                />
            )}
        </>
    );
};

// Get Purchase Guest details if given participant is already linked
export const getLinkedGuest = (participant: WaiverParticipant, guests?: Guest[]) => {
    return guests?.find((guest) => guest.participant === participant.id);
};
